import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { FadeUp, Overline } from "@/components/FadeUp";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useLocalizeModel } from "@/i18n/useLocalizeModel";
import {
  Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious,
} from "@/components/ui/carousel";

export default function Gallery() {
  const [models, setModels] = useState([]);
  const { t } = useTranslation();
  const localize = useLocalizeModel();

  useEffect(() => {
    api.get("/models").then((r) => setModels(r.data)).catch(() => {});
  }, []);

  if (models.length === 0) {
    return <div className="max-w-7xl mx-auto px-6 lg:px-10 py-32 text-slate-500">{t("modelsPage.loading")}</div>;
  }

  return (
    <div data-testid="gallery-page">
      <section className="py-16 md:py-24 border-b border-slate-200 bg-[#F4F7F9]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Overline>{t("galleryPage.overline")}</Overline>
          <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tighter text-[#0A2540] max-w-2xl">
            {t("galleryPage.title")}
          </h1>
          <p className="mt-5 text-base md:text-lg text-slate-600 max-w-xl leading-relaxed">
            {t("galleryPage.subtitle")}
          </p>
        </div>
      </section>

      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Tabs defaultValue={models[0].slug}>
            <TabsList className="bg-[#F4F7F9] border border-slate-200 rounded-md flex-wrap h-auto gap-1 p-1">
              {models.map((m) => (
                <TabsTrigger key={m.slug} value={m.slug} data-testid={`gallery-tab-${m.slug}`}
                             className="rounded-sm px-4 py-2 text-sm font-semibold">
                  {m.name}
                </TabsTrigger>
              ))}
            </TabsList>

            {models.map((raw) => {
              const m = localize(raw);
              const slides = [m.image, ...(m.gallery || [])];
              return (
                <TabsContent key={m.slug} value={m.slug} className="mt-8">
                  <FadeUp>
                    <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                      <div>
                        <p className="text-xs tracking-[0.2em] uppercase font-semibold text-slate-500">{m.category}</p>
                        <h2 className="mt-1 text-3xl md:text-4xl font-bold tracking-tight text-[#0A2540]">{m.name}</h2>
                      </div>
                      <Button asChild variant="outline"
                              className="rounded-md border-[#0A2540] text-[#0A2540] hover:bg-[#0A2540] hover:text-white px-6 py-3 shrink-0">
                        <Link to={`/modelle/${m.slug}`} data-testid={`gallery-explore-${m.slug}`}>
                          {t("galleryPage.explore")} <ArrowRight className="w-4 h-4 ml-2" />
                        </Link>
                      </Button>
                    </div>

                    <Carousel className="mt-6" opts={{ loop: true }}>
                      <CarouselContent>
                        {slides.map((src, i) => (
                          <CarouselItem key={`${m.slug}-${i}`}>
                            <div className="border border-slate-200 bg-[#EAF0F6] overflow-hidden">
                              <img src={src} alt={`${m.name} – Bild ${i + 1}`}
                                   data-testid={`gallery-slide-${m.slug}-${i}`}
                                   className="w-full aspect-[16/9] object-cover" />
                            </div>
                          </CarouselItem>
                        ))}
                      </CarouselContent>
                      <CarouselPrevious data-testid={`gallery-prev-${m.slug}`}
                                        className="left-4 bg-white/90 border-slate-200 hover:bg-white" />
                      <CarouselNext data-testid={`gallery-next-${m.slug}`}
                                    className="right-4 bg-white/90 border-slate-200 hover:bg-white" />
                    </Carousel>
                    <div className="mt-3 flex gap-2 overflow-x-auto pb-2" data-testid={`gallery-thumbs-${m.slug}`}>
                      {slides.map((src, i) => (
                        <img key={i} src={src} alt="" loading="lazy"
                             className="h-16 w-24 object-cover border border-slate-200 shrink-0" />
                      ))}
                    </div>

                    {(m.video || m.youtube || (m.youtube_videos || []).length > 0) && (
                      <div className="mt-10">
                        <Overline>{t("galleryPage.videoTitle")}</Overline>
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-6">
                          {m.video && (
                            <video src={m.video} poster={m.image} controls preload="metadata"
                                   data-testid={`gallery-video-${m.slug}`}
                                   className={`w-full border border-slate-200 bg-black aspect-video ${(m.youtube_videos || []).length === 0 && !m.youtube ? "md:col-span-2" : ""}`} />
                          )}
                          {(m.youtube_videos || (m.youtube ? [m.youtube] : [])).map((vid) => (
                            <iframe key={vid} src={`https://www.youtube-nocookie.com/embed/${vid}`}
                                    title={`${m.name} – Video`} allowFullScreen
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    data-testid={`gallery-youtube-${m.slug}-${vid}`}
                                    className="w-full border border-slate-200 aspect-video" />
                          ))}
                        </div>
                      </div>
                    )}
                  </FadeUp>
                </TabsContent>
              );
            })}
          </Tabs>
        </div>
      </section>
    </div>
  );
}
